import { useEffect, useMemo } from 'react'

import { useLazyGetAllCSNotesQuery } from 'api/notes/configuredServiceNotesCrud'
import { useLazyGetAllLocationNotesQuery } from 'api/notes/locationNotesCrud'
import { useLazyGetAllWorkOrderNotesQuery } from 'api/notes/workOrderNotesCrud'
import { getArchivedAndUnarchivedNotes, getSortedNotes } from 'data/notes/notesSelectors'
import { NOTE_TYPES } from 'settings/constants/note'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

const { WORK_ORDER, LOCATION, CONFIGURED_SERVICE } = NOTE_TYPES as Record<string, string>

export type NoteRecord = Record<string, unknown> & { id?: string }
export type MergedNote = NoteRecord & { noteTypeTag: string }

type UseCombinedNotesArgs = {
  // Drives the fetch, so a closed dialog or an unhovered badge costs no requests.
  enabled: boolean
  workOrderId?: string
  locationId?: string
  configuredServiceId?: string
}

const sortUnarchived = (notesData: { notes?: unknown[] } | undefined): NoteRecord[] => {
  const sorted = getSortedNotes({ notes: notesData?.notes ?? [] })
  const { unarchivedNotes } = getArchivedAndUnarchivedNotes({ notes: sorted })
  return unarchivedNotes
}

/**
 * Every note kind an entity carries: one pinned-first list tagged by kind, plus the per-kind
 * slices. Only the ids passed in are fetched, so a surface holding a subset gets that subset.
 */
const useCombinedNotes = ({ enabled, workOrderId, locationId, configuredServiceId }: UseCombinedNotesArgs) => {
  const [getAllWorkOrderNotes, woNotesResult] = useLazyGetAllWorkOrderNotesQuery()
  const [getAllLocationNotes, locationNotesResult] = useLazyGetAllLocationNotesQuery()
  const [getAllCSNotes, csNotesResult] = useLazyGetAllCSNotesQuery()

  // `currentData`, not `data`: `data` keeps the previous ids' notes while the next request is pending.
  const woNotes = useMemo(() => (workOrderId ? sortUnarchived(woNotesResult.currentData) : []), [workOrderId, woNotesResult.currentData])
  const locationNotes = useMemo(
    () => (locationId ? sortUnarchived(locationNotesResult.currentData) : []),
    [locationId, locationNotesResult.currentData]
  )
  const csNotes = useMemo(
    () => (configuredServiceId ? sortUnarchived(csNotesResult.currentData) : []),
    [configuredServiceId, csNotesResult.currentData]
  )

  const mergedNotes = useMemo<MergedNote[]>(() => {
    const tagged = [
      ...csNotes.map(note => ({ ...note, noteTypeTag: CONFIGURED_SERVICE })),
      ...locationNotes.map(note => ({ ...note, noteTypeTag: LOCATION })),
      ...woNotes.map(note => ({ ...note, noteTypeTag: WORK_ORDER })),
    ]
    return getSortedNotes({ notes: tagged }) as MergedNote[]
  }, [csNotes, locationNotes, woNotes])

  // A requested kind that hasn't been triggered yet counts as pending, so a preview shows its
  // skeleton instead of flashing "no notes" before the first request leaves.
  const isFetching =
    enabled &&
    ((Boolean(workOrderId) && (woNotesResult.isUninitialized || woNotesResult.isFetching)) ||
      (Boolean(locationId) && (locationNotesResult.isUninitialized || locationNotesResult.isFetching)) ||
      (Boolean(configuredServiceId) && (csNotesResult.isUninitialized || csNotesResult.isFetching)))

  useEffect(() => {
    if (!enabled) return
    if (workOrderId) getAllWorkOrderNotes({ id: workOrderId }, true).unwrap().catch(handleError)
    if (locationId) getAllLocationNotes({ id: locationId }, true).unwrap().catch(handleError)
    if (configuredServiceId) getAllCSNotes({ id: configuredServiceId }, true).unwrap().catch(handleError)
  }, [enabled, workOrderId, locationId, configuredServiceId, getAllWorkOrderNotes, getAllLocationNotes, getAllCSNotes])

  return { mergedNotes, woNotes, locationNotes, csNotes, isFetching }
}

export default useCombinedNotes
